import { useCallback, useEffect, useState } from "react";
import type {
  ClinicianMeetingOffer,
  MeetingProductKey,
  StaffBookMeetingResult,
  StaffBookableMeetingReason,
  StaffMeetingBookingOptions,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  bookPatientMeeting,
  cancelMeetingOffer,
  fetchMeetingBookingOptions,
  fetchMeetingOffers,
} from "@/lib/meeting-booking-api";
import {
  type CareMeetingBookingAccess,
  MEETING_OFFER_STATUS_LABEL,
  careMeetingBookingAccess,
  clinicStartIso,
  priceText,
} from "@/lib/meeting-booking";
import { meetingReasonLabel } from "@/lib/meeting-labels";
import { useAuth } from "@/auth/AuthContext";
import { useReadOnly } from "./SupportView";

/** TES-1219 — see careMeetingBookingAccess. */
export function useCareMeetingBookingAccess(): CareMeetingBookingAccess {
  const { claims } = useAuth();
  const inSupportView = useReadOnly();
  return careMeetingBookingAccess({
    role: claims?.role,
    roles: claims?.roles,
    inSupportView,
  });
}

// TES-1168 (A-02, canonical addendum 2026-10-08, function 1) — "Boka nytt
// vårdmöte" on the patient overview.
//
// "Behörig vårdpersonal anger vårdgivare, mötestyp, datum, tid, möteslängd
// samt om mötet är kostnadsfritt eller avgiftsbelagt. Vid avgift väljs en
// fördefinierad och versionsstyrd mötesprodukt med fastställt pris."
//
// So the price is never typed: a paid meeting picks one of the server's
// products, and the amount shown is the product's. A free meeting is booked
// at once; a paid one waits for the patient to confirm and pay in the app,
// and is listed below until she has.

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

function BookingForm({
  patientId,
  options,
  onBooked,
  onUnauthorized,
}: {
  patientId: string;
  options: StaffMeetingBookingOptions;
  onBooked: (result: StaffBookMeetingResult) => void;
  onUnauthorized?: () => void;
}) {
  const [providerId, setProviderId] = useState(options.providers[0]?.id ?? "");
  const [reason, setReason] = useState<StaffBookableMeetingReason>(
    options.reasons[0] ?? "follow_up",
  );
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(
    options.durations.includes(30) ? 30 : (options.durations[0] ?? 30),
  );
  const [paid, setPaid] = useState(false);
  const [productKey, setProductKey] = useState<MeetingProductKey | "">(
    options.products[0]?.key ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startAt = clinicStartIso(date, time);
  const canSubmit =
    providerId !== "" &&
    startAt !== null &&
    (!paid || productKey !== "") &&
    !busy;

  async function submit() {
    if (!startAt) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bookPatientMeeting(patientId, {
        providerId,
        reason,
        startAt,
        durationMinutes: duration,
        payment:
          paid && productKey !== ""
            ? { kind: "product", productKey }
            : { kind: "free" },
      });
      onBooked(result);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  const label = "flex flex-col gap-[2px] text-sm";
  const input =
    "rounded-sm border border-surfaceMuted bg-surface px-sm py-xs text-sm";

  return (
    <form
      className="mt-sm flex flex-col gap-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className={label}>
        Vårdgivare
        <select
          className={input}
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          {options.providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </select>
      </label>
      <label className={label}>
        Mötestyp
        <select
          className={input}
          value={reason}
          onChange={(e) =>
            setReason(e.target.value as StaffBookableMeetingReason)
          }
        >
          {options.reasons.map((r) => (
            <option key={r} value={r}>
              {meetingReasonLabel(r)}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-sm">
        <label className={`${label} flex-1`}>
          Datum
          <input
            type="date"
            className={input}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className={`${label} flex-1`}>
          Tid
          <input
            type="time"
            className={input}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
          />
        </label>
      </div>
      <label className={label}>
        Möteslängd
        <select
          className={input}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
        >
          {options.durations.map((d) => (
            <option key={d} value={d}>
              {d} min
            </option>
          ))}
        </select>
      </label>
      <fieldset className="flex flex-col gap-xs text-sm">
        <legend className="mb-[2px]">Kostnad</legend>
        <label className="flex items-center gap-xs">
          <input
            type="radio"
            name="meeting-cost"
            checked={!paid}
            onChange={() => setPaid(false)}
          />
          Kostnadsfritt
        </label>
        <label className="flex items-center gap-xs">
          <input
            type="radio"
            name="meeting-cost"
            checked={paid}
            onChange={() => setPaid(true)}
            disabled={options.products.length === 0}
          />
          Avgiftsbelagt
        </label>
        {paid ? (
          <>
            <select
              className={input}
              value={productKey}
              onChange={(e) =>
                setProductKey(e.target.value as MeetingProductKey)
              }
              aria-label="Mötesprodukt"
            >
              {options.products.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} · {priceText(p.amountMinor, p.currency)}
                </option>
              ))}
            </select>
            <p className="text-xs text-textSecondary">
              Patienten bekräftar och betalar i appen innan mötet blir bokat
              (appversion 2.0.8 eller senare). Tiden är reserverad tills mötet
              börjar.
            </p>
          </>
        ) : (
          <p className="text-xs text-textSecondary">
            Mötet bokas direkt. Patienten får en notis och en kalenderinbjudan,
            och behöver inte betala eller fylla i något nytt.
          </p>
        )}
      </fieldset>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        className="self-start rounded-sm bg-brand px-md py-xs text-sm text-white disabled:opacity-50"
        disabled={!canSubmit}
      >
        {busy ? "Bokar…" : "Boka vårdmöte"}
      </button>
    </form>
  );
}

export function BookCareMeeting({
  patientId,
  onBooked,
  onUnauthorized,
}: {
  patientId: string;
  onBooked: () => void;
  onUnauthorized?: () => void;
}) {
  // TES-1195 / TES-1219 (Ö-396) — the doctor books, and so does the admin,
  // "som administrativ hjälp": in her support views and, with the admin
  // grant, from her own coach page. The plain coach never books.
  const access = useCareMeetingBookingAccess();
  const bookable = access !== "none";
  const [options, setOptions] = useState<StaffMeetingBookingOptions | null>(
    null,
  );
  const [offers, setOffers] = useState<ClinicianMeetingOffer[]>([]);
  const [open, setOpen] = useState(false);
  const [readback, setReadback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadOffers = useCallback(() => {
    fetchMeetingOffers(patientId)
      .then((r) => setOffers(r.offers))
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) onUnauthorized?.();
        else setError(errorText(e instanceof Error ? e.message : String(e)));
      });
  }, [patientId, onUnauthorized]);

  // TES-1219 — a coach with the admin grant reads the offers as the admin,
  // on today's support reason: only once she opens the form, so the reason
  // dialog never comes up merely because she opened a patient she coaches.
  const shouldLoadOffers = access !== "book_on_open" || open;
  useEffect(() => {
    if (shouldLoadOffers) loadOffers();
  }, [loadOffers, shouldLoadOffers]);

  useEffect(() => {
    if (!bookable || !open || options) return;
    fetchMeetingBookingOptions()
      .then(setOptions)
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) onUnauthorized?.();
        else setError(errorText(e instanceof Error ? e.message : String(e)));
      });
  }, [bookable, open, options, onUnauthorized]);

  async function cancel(offerId: string) {
    setError(null);
    try {
      const r = await cancelMeetingOffer(patientId, offerId);
      setReadback(
        r.refunded
          ? "Mötet är återkallat och avgiften återbetald."
          : "Mötet är återkallat.",
      );
      loadOffers();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(errorText(e instanceof Error ? e.message : String(e)));
    }
  }

  // TES-1199 (Ö-405) — a time she declined stays in view while it is still
  // ahead, so the care team sees it and can propose a new one.
  const now = Date.now();
  const inView = (o: ClinicianMeetingOffer) =>
    o.status === "awaiting_payment" ||
    (o.status === "declined" && new Date(o.scheduledAt).getTime() > now);
  const waiting = offers.filter(inView);
  const past = offers.filter((o) => !inView(o));
  if (!bookable && offers.length === 0) return null;

  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">Nytt vårdmöte</h2>
      {readback ? (
        <p className="mb-xs rounded-sm border border-brand/30 bg-brand/5 p-sm text-sm">
          {readback}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mb-xs text-sm text-danger">
          {error}
        </p>
      ) : null}
      {waiting.length > 0 ? (
        <ul className="mb-sm flex flex-col gap-xs text-sm">
          {waiting.map((o) => (
            <li key={o.id} className="flex flex-col gap-[2px]">
              <span>
                {fmt(o.scheduledAt)} · {o.provider.displayName} ·{" "}
                {meetingReasonLabel(o.reason)}
              </span>
              <span className="text-xs text-textSecondary">
                {MEETING_OFFER_STATUS_LABEL[o.status]} · {o.product.label} ·{" "}
                {priceText(o.product.amountMinor, o.product.currency)}
              </span>
              {bookable && o.status === "awaiting_payment" ? (
                <button
                  type="button"
                  className="self-start text-xs text-brand hover:underline"
                  onClick={() => void cancel(o.id)}
                >
                  Återkalla
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {past.length > 0 ? (
        <details className="mb-sm text-sm">
          <summary className="cursor-pointer text-textSecondary">
            Tidigare avgiftsbelagda bokningar
          </summary>
          <ul className="mt-xs flex flex-col gap-xs">
            {past.map((o) => (
              <li key={o.id} className="text-xs text-textSecondary">
                {fmt(o.scheduledAt)} · {o.provider.displayName} ·{" "}
                {MEETING_OFFER_STATUS_LABEL[o.status]}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {bookable ? (
        <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
          <summary className="cursor-pointer text-sm text-brand">
            Boka nytt vårdmöte
          </summary>
          {options ? (
            <BookingForm
              key={readback ?? "form"}
              patientId={patientId}
              options={options}
              onUnauthorized={onUnauthorized}
              onBooked={(result) => {
                setReadback(
                  result.kind === "booked"
                    ? `Vårdmötet ${fmt(result.scheduledAt)} är bokat. Patienten har fått en notis och en kalenderinbjudan.`
                    : `Patienten har fått en notis om att bekräfta och betala mötet ${fmt(result.scheduledAt)}. Tiden är reserverad tills mötet börjar.`,
                );
                setOpen(false);
                loadOffers();
                onBooked();
              }}
            />
          ) : open && !error ? (
            <p className="mt-sm text-sm text-textSecondary">Laddar…</p>
          ) : null}
        </details>
      ) : null}
    </section>
  );
}
